import {
  type WithdrawalAmountValidation,
  deductFlatFee,
  isWithdrawalAddressSupported,
  useDebouncedValue,
} from '@funkit/connect-core'
import type { TFunction } from 'i18next'
import { useMemo, useState } from 'react'
import { zeroAddress } from 'viem'

import type { WithdrawalFlowConfig } from '../flow/withdrawalFlowConfig'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { resolveWithdrawalSourceTokens } from './sourceToken'
import { useCryptoWithdrawalQuote } from './useCryptoWithdrawalQuote'
import { useCryptoWithdrawalValidation } from './useCryptoWithdrawalValidation'
import { useSourceTokenDecimals } from './useSourceTokenDecimals'
import { useSourceTokenUsdPrice } from './useSourceTokenUsdPrice'
import { useWithdrawalAmountInput } from './useWithdrawalAmountInput'
import { useWithdrawalDestinationTokens } from './useWithdrawalDestinationTokens'
import { useWithdrawalRisk } from './useWithdrawalRisk'
import { useWithdrawalSourceTokenBalance } from './useWithdrawalSourceTokenBalance'
import { type WithdrawCtaBlocker, isAmountBelowQuoteFee } from './validation'

// Long enough that a typed amount settles before it is priced, short enough
// that the summary doesn't feel detached from the keystroke (web uses the same).
const AMOUNT_DEBOUNCE_MS = 300

/**
 * What the recipient step owns. The rest of what `getWithdrawCtaBlocker` reports
 * is the amount's business, and holding the recipient step on it would strand
 * the user on a screen with nothing to fix.
 */
const RECIPIENT_BLOCKERS = new Set<WithdrawCtaBlocker>([
  'enter-recipient',
  'unsupported-recipient',
  'recipient-same-as-source',
  'select-destination',
  'invalid-for-chain',
])

/** What the crypto withdrawal's screens read and write. */
export type CryptoWithdrawalForm = ReturnType<typeof useCryptoWithdrawalForm>

/**
 * One withdrawal in progress, across the screens that build it: the amount, the
 * recipient and the fees are asked for a step at a time, and all of it is priced
 * against the same quote.
 *
 * Held above the screens rather than inside one of them, so a step can be added
 * (or reordered) without the state moving house — and so the quote isn't thrown
 * away and re-fetched on every navigation.
 */
export function useCryptoWithdrawalForm({
  config,
  quoteInputFeeTokens,
  quoteInputFeeSettled = true,
}: {
  config: WithdrawalFlowConfig
  /**
   * A flat fee the venue takes off the withdrawal before it is quoted, in
   * source-token units, or 0.
   */
  quoteInputFeeTokens: number
  /** See `WithdrawalFlowConfig.quoteInputFeeSettled`. */
  quoteInputFeeSettled?: boolean
}) {
  const { t } = useFunkitTranslation()
  const [amount, setAmount] = useState('')
  const [recipient, setRecipient] = useState('')

  const sourceOptions = resolveWithdrawalSourceTokens(config)
  // By index, not by symbol: two options can be the same symbol on the same
  // chain and differ only in which balance they draw from.
  const [sourceIndex, setSourceIndex] = useState(0)
  // A host can swap `sourceTokens` under us; falling back without clamping the
  // index would leave the picker with no row checked.
  const selectedSourceIndex =
    sourceIndex < sourceOptions.length ? sourceIndex : 0
  const sourceOption = sourceOptions[selectedSourceIndex]
  const sourceToken = sourceOption.token
  const sourceTokenParams = useMemo(() => ({ sourceToken }), [sourceToken])
  const sourceLabel = sourceOption.label ?? sourceToken.symbol
  const balance = useWithdrawalSourceTokenBalance(
    config.withdrawalSourceTokenBalance,
    sourceTokenParams,
  )

  // The destination is the point of this rail: the relay quote prices a swap
  // and/or a bridge, so receiving a different asset on a different chain is the
  // normal case, not an edge one.
  const {
    chains: destinationChains,
    destinationToken,
    selectDestinationToken,
    isLoading: isLoadingDestinations,
  } = useWithdrawalDestinationTokens({
    sourceToken,
    defaultReceiveToken: config.defaultReceiveToken,
  })

  const { isRecipientBlacklisted, amountValidation, ctaBlocker } =
    useCryptoWithdrawalValidation({
      config,
      sourceToken,
      destinationToken,
      amount,
      recipient,
      balance,
    })

  // Screening is address-intrinsic, so it runs off the typed address rather
  // than waiting for the amount.
  const risk = useWithdrawalRisk(
    recipient,
    destinationToken ? Number(destinationToken.chainId) : undefined,
  )

  // Hold the quote while the user is mid-keystroke, and never price something
  // we already know we would refuse to submit.
  const { debounced: debouncedAmount, isSettled } = useDebouncedValue(
    amount,
    AMOUNT_DEBOUNCE_MS,
  )

  // The account is debited what the user typed; relay moves what is left after
  // the venue's flat fee, so that is what gets priced and signed.
  const quoteAmount = deductFlatFee(debouncedAmount, quoteInputFeeTokens)
  // Shared with the quote's own read (same query key), so this costs no extra
  // call — it is what tells a sub-base-unit remainder from a real one.
  const sourceDecimals = useSourceTokenDecimals(sourceToken)
  // Shares the validator's own read (same query key), so this costs no extra
  // call and prices the entry off the rate the limits are judged against.
  const { data: sourceUsdPrice } = useSourceTokenUsdPrice(sourceToken)
  // Held here rather than in the screen, which unmounts on the way to the
  // recipient and back.
  const amountInput = useWithdrawalAmountInput({
    amount,
    setAmount,
    sourceUsdPrice,
    sourceDecimals,
  })
  // Read off the typed amount, not the debounced one, so the message lands with
  // the keystroke. Validation passes on the gross amount, so without this a fee
  // that eats it leaves nothing to quote and no reason on screen.
  const isAmountBelowFee = isAmountBelowQuoteFee({
    amount,
    feeTokenAmount: quoteInputFeeTokens,
    decimals: sourceDecimals,
  })

  const {
    quote,
    isLoading: isQuoteLoading,
    isError: isQuoteError,
  } = useCryptoWithdrawalQuote({
    sourceToken,
    destinationToken,
    recipientAddress: recipient,
    amount: quoteAmount,
    // The wallet doesn't spend on a venue rail, and the address that does isn't
    // minted until submit — a placeholder keeps Relay off a wallet-to-itself route.
    // A walletless config quotes against that same placeholder (web parity).
    walletAddress: config.submitWithdrawal
      ? zeroAddress
      : (config.withdrawalWallet?.address() ?? zeroAddress),
    // Everything the form already knows is wrong, plus the async verdict:
    // `isRisky === false` is required, so in-flight and errored both hold. A
    // venue fee that hasn't settled yet would move `quoteAmount` underneath
    // this query, so it holds too.
    enabled:
      !ctaBlocker &&
      !isAmountBelowFee &&
      isSettled &&
      risk.isRisky === false &&
      quoteInputFeeSettled,
  })

  // What the recipient ends up with, in the currency the screen leads with. The
  // token amount is what the quote returns; the price is what turns it into the
  // number beside it. `0` from the price hook means unknown, not free.
  const { data: destinationPrice } = useSourceTokenUsdPrice(
    destinationToken ?? sourceToken,
  )
  // Mid-edit the query is disabled and `quote` still holds the previous amount's
  // price, so the figures are withheld until the inputs settle rather than
  // presenting the old estimate as this withdrawal's.
  const settledQuote = isSettled ? quote : undefined
  const receiveAmount = settledQuote?.receiveAmount
  const receiveUsd =
    destinationToken && receiveAmount && destinationPrice > 0
      ? Number(receiveAmount) * destinationPrice
      : undefined
  // The floor the trade won't execute below — the received amount less the
  // slippage the quote was raised with.
  const receiveFloorUsd =
    receiveUsd === undefined || settledQuote === undefined
      ? undefined
      : receiveUsd * (1 - settledQuote.fees.maxSlippage / 100)

  return {
    amount,
    setAmount,
    // The amount as it is being typed; see `useWithdrawalAmountInput`.
    ...amountInput,
    recipient,
    setRecipient,

    sourceOptions,
    selectedSourceIndex,
    sourceOption,
    sourceToken,
    sourceLabel,
    sourceDecimals,
    balance,
    /**
     * Records the decision and clears the amount: it was in the previous token's
     * units, and the balance and minimum it was checked against are the new
     * token's. Returns whether the source actually moved.
     */
    selectSource: (index: number) => {
      if (index === selectedSourceIndex) {
        return false
      }
      const picked = sourceOptions[index]
      // Source decision point — which balance the withdrawal draws from.
      logger.info('connect-rn:withdrawal:source_selected', {
        fromChainId: picked?.token.chainId,
        fromToken: picked?.token.address,
        sourceLabel: picked?.label ?? picked?.token.symbol,
      })
      setSourceIndex(index)
      setAmount('')
      return true
    },

    destinationChains,
    destinationToken,
    selectDestinationToken,
    isLoadingDestinations,

    isRecipientBlacklisted,
    amountValidation,
    ctaBlocker,
    risk,

    /**
     * Whether what has been typed is a wallet address at all — `undefined` while
     * the field is empty. Being one doesn't make it usable: the blacklist and the
     * destination's chain are the separate verdicts in `recipientError`.
     */
    isRecipientAddress:
      recipient === '' ? undefined : isWithdrawalAddressSupported(recipient),
    /** Why a well-formed address still can't be used. */
    recipientError: resolveRecipientErrorText(t, ctaBlocker),
    /** Screening's non-blocking verdict; the submit is what it gates. */
    recipientWarning: risk.isRisky
      ? t('withdrawCrypto.riskyAddress')
      : undefined,
    /** Enough to leave the recipient step, which the amount's problems don't hold. */
    canConfirmRecipient: !ctaBlocker || !RECIPIENT_BLOCKERS.has(ctaBlocker),
    /**
     * Fail closed: only a completed, clean screening lets the withdrawal through,
     * and only while the quote on screen is the one for what the field says —
     * mid-edit the query is disabled and `quote` still holds the last price.
     */
    canSubmit:
      !ctaBlocker &&
      !isAmountBelowFee &&
      isSettled &&
      risk.isRisky === false &&
      quoteInputFeeSettled &&
      !!quote,

    quote,
    quoteAmount,
    /** Destination-token units, absent until the quote matches what is typed. */
    receiveAmount,
    receiveUsd,
    receiveFloorUsd,
    isQuoteLoading,
    isQuoteError,
    isSettled,
    isAmountBelowFee,

    /**
     * The amount step's own gate: `ctaBlocker` also weighs the recipient, which
     * isn't asked for until the step after.
     */
    amountError: resolveAmountErrorText({
      t,
      amount,
      amountValidation,
      tokenSymbol: sourceToken.symbol,
      // A per-token floor is already in source-token units; without one the floor
      // came from `getMinWithdrawalUSD`, so it reads as currency.
      isMinInTokenUnits:
        config.getMinWithdrawalAmount?.({ sourceToken }) !== undefined,
      isAmountBelowFee,
      quoteInputFeeTokens,
    }),
    canContinue:
      !!amount &&
      amountValidation.isWithdrawAmountValid &&
      !isAmountBelowFee &&
      !!destinationToken,
  }
}

/** The recipient's own blockers, as the message under the field. */
function resolveRecipientErrorText(
  t: TFunction,
  blocker: WithdrawCtaBlocker | undefined,
): string | undefined {
  // `enter-recipient` is left to the field's own verdict, which already says the
  // address isn't one.
  if (blocker === 'unsupported-recipient') {
    return t('withdrawCrypto.unsupportedRecipient')
  }
  if (blocker === 'recipient-same-as-source') {
    return t('withdrawCrypto.ctaRecipientSameAsSource')
  }
  if (blocker === 'invalid-for-chain') {
    return t('withdrawCrypto.ctaInvalidForChain')
  }
  return undefined
}

function resolveAmountErrorText({
  t,
  amount,
  amountValidation,
  tokenSymbol,
  isMinInTokenUnits,
  isAmountBelowFee,
  quoteInputFeeTokens,
}: {
  t: TFunction
  amount: string
  amountValidation: WithdrawalAmountValidation
  tokenSymbol: string
  /** See {@link WithdrawalAmountValidation.minWithdrawalDisplayValue}. */
  isMinInTokenUnits: boolean
  /** The venue's fee leaves nothing for the quote to price. */
  isAmountBelowFee: boolean
  quoteInputFeeTokens: number
}): string | undefined {
  if (!amount) {
    return undefined
  }
  if (amountValidation.isWithdrawAmountTooLow) {
    // The floor is either denomination, so it has to be labelled as the one it
    // is: a USD floor shown as token units reads ~4000× off on an ETH source.
    const min = amountValidation.minWithdrawalDisplayValue
    return t('withdrawCrypto.belowMinimum', {
      amount: isMinInTokenUnits
        ? `${min} ${tokenSymbol}`
        : `$${min.toFixed(2)}`,
    })
  }
  if (amountValidation.isWithdrawAmountAboveBalance) {
    return t('withdrawCrypto.aboveBalance')
  }
  // After the minimum, which carries the more useful number when both apply.
  if (isAmountBelowFee) {
    return t('withdrawCrypto.belowFee', {
      amount: `${quoteInputFeeTokens} ${tokenSymbol}`,
    })
  }
  return undefined
}
